import { useEffect, useState } from "react";
import { isEmpty } from "lodash-es";
import { useNavigate } from "react-router";
import { PATHS } from "lib/routing";
import { Box, CircularProgress, Typography, Card } from "@mui/material";
import Page from "lib/components/layout/Page/Page";
import VerticalSpreadLayout from "lib/components/layout/VerticalSpreadLayout/VerticalSpreadLayout";
import IconLink from "lib/components/links/IconLink/IconLink";
import UserProfile from "ui/appointment/components/common/user/UserProfile/UserProfile";
import ButtonsFooter from "lib/components/buttons/ButtonsFooter/ButtonsFooter";
import ConfirmationModal from "lib/components/modals/ConfirmationModal/ConfirmationModal";
import TcuConfirmationCard from "./TcuConfirmationCard";
import { sxStyles } from "./TcuConfirmationPage.styles";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import { useGetMessageAction } from "lib/routing/messageChannel/hooks/useGetMessageAction";
import {
  selectCreateAppointment,
  selectCreateTcuAppointment,
} from "ui/appointment/ducks/selectors";
import { selectUser } from "lib/redux/user/selectors";
import { getDateFromISOString } from "lib/util/DateTimeUtil/getDateFromISOString/getDateFromISOString";
import { useGetTcuAvailableSlots } from "ui/appointment/hooks/useGetTcuAvailableSlots";
import {
  TcuAvailableSlot,
  TcuSlotDetails,
} from "api/appointment/GetTcuAvailableSlots/getTcuAvailableSlots.toUi.types";
import { AxiosError } from "axios";
import { cancelTcuAppointment } from "api/appointment/CancelAppointment/cancelAppointment";
import { ErrorModalTitles } from "lib/components/navigation/AppRouteRenderer/ErrorModal/HelperFunctions";
import MapRawStringToReactElement from "lib/util/ReactComponentUtil/mapRawStringToReactElement/MapRawStringtoReactElement";
import {
  setTcuSelectedRegInstitutionCode,
  updateTcuRemainingRegInstitutionCodes,
  updateTcuOtherSlots,
  updateTcuBookedSlot,
} from "ui/appointment/ducks/appointmentsSlice";
import { logEventToGoogleAnalytics } from "lib/util/GoogleAnalyticsUtil/logEvent";
import { EVENTS } from "lib/util/GoogleAnalyticsUtil/events";
import { navigateToExternalInMobileBrowser } from "lib/routing/navigate/navigate";

const TcuConfirmationPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const classes = sxStyles();

  // Redux States
  const { memberIdentifier } = useAppSelector(selectUser);
  const {
    selectedRegInstitutionCode,
    remainingRegInstitutionCode,
    feedbackUrl,
  } = useAppSelector(selectCreateTcuAppointment);
  const { selectedInstitution, selectedInstitutionCode } = useAppSelector(
    selectCreateAppointment,
  );
  const institution = selectedInstitution || "";

  // Local states
  const [open, setOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [bookedSlot, setBookedSlot] = useState<TcuAvailableSlot | null>(null);
  const [pageRefresh, setPageRefresh] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [errorMessageDisplay, setErrorMessageDisplay] = useState<string | null>(
    "",
  );
  const { messageActionObj, consumeMessageActionObj } = useGetMessageAction();

  useEffect(() => {
    if (messageActionObj?.action === "backTriggered") {
      navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
      consumeMessageActionObj();
    }
  }, [consumeMessageActionObj, dispatch, messageActionObj?.action, navigate]);

  // GetTcuAvailableSlot call
  const { availableSlots, isLoading, hasErrored, errorMessage } =
    useGetTcuAvailableSlots(
      pageRefresh,
      memberIdentifier,
      selectedInstitutionCode || "",
      selectedRegInstitutionCode || "",
    );

  useEffect(() => {
    // 1st slot returned is the booked appointment slot
    const bookedApptSlot =
      availableSlots != null &&
      availableSlots.length > 0 &&
      availableSlots[0].slots.length > 0
        ? availableSlots[0]
        : null;
    setBookedSlot(bookedApptSlot);

    if (hasErrored || (!isEmpty(isLoading) && isEmpty(bookedApptSlot))) {
      logEventToGoogleAnalytics(EVENTS.VIEW_TCU_ERROR_PROMPT);
      setIsErrorModalOpen(true);
      setErrorMessageDisplay(errorMessage);
    }
  }, [availableSlots, errorMessage, hasErrored, isLoading]);

  // Cancel appt
  const handleCancelAppt = async () => {
    logEventToGoogleAnalytics(EVENTS.CLICK_TCU_APPT_OTHER_CANCEL);
    try {
      setIsCancelling(true);
      await cancelTcuAppointment({
        memberIdentifier,
        slotIds: !isEmpty(bookedSlot)
          ? bookedSlot.slots.map((slot: TcuSlotDetails) => slot.id)
          : [],
        institutionCode: selectedInstitutionCode,
      });
      navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
    } catch (error) {
      setIsErrorModalOpen(true);
      if (error instanceof AxiosError) {
        setErrorMessageDisplay(error.response?.data.Message);
      }
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Page>
      {isLoading === true || isCancelling ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isErrorModalOpen ? (
        <VerticalSpreadLayout>
          <Box>
            <UserProfile showMakeAppointmentButton={false} />
            <ConfirmationModal
              title={ErrorModalTitles.oops}
              open={isErrorModalOpen}
              isNextButtonDisabled={!feedbackUrl}
              body={MapRawStringToReactElement(errorMessageDisplay || "")} // Can be either from error in GetTcuAvailableSlots or CancelAppointment
              nextButtonText={"Make offline request"}
              onClickNext={() => {
                logEventToGoogleAnalytics(
                  EVENTS.CLICK_TCU_ERROR_PROMPT_OFFLINEREQ,
                );

                if (feedbackUrl) {
                  navigateToExternalInMobileBrowser(feedbackUrl);
                }
              }}
              cancelButtonText="Go to Appointment"
              onClickCancel={() => {
                navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
              }}
            />
          </Box>
        </VerticalSpreadLayout>
      ) : (
        <>
          <VerticalSpreadLayout>
            <Box>
              <UserProfile showMakeAppointmentButton={false} />
              <Box sx={classes.detailsLayout}>
                <Typography sx={classes.titleText}>
                  Booked Appointment(s) Details
                </Typography>
                {bookedSlot &&
                  bookedSlot.slots.map(
                    (slot: TcuSlotDetails, index: number) => (
                      <Card sx={classes.root} key={index}>
                        <TcuConfirmationCard
                          slotDateTime={
                            getDateFromISOString(slot.date) || new Date()
                          }
                          institution={institution}
                          service={slot.serviceType}
                        />
                      </Card>
                    ),
                  )}
              </Box>
              {
                // When there is more than one available slot
                !isEmpty(bookedSlot) && availableSlots.length > 1 && (
                  <Box sx={classes.iconLinkContainer}>
                    <IconLink
                      title={"Choose other time slot"}
                      iconPosition="end"
                      icon="arrowRightOutline"
                      onClick={() => {
                        logEventToGoogleAnalytics(
                          EVENTS.CLICK_TCU_APPT_OTHER_TIMESLOTS,
                        );
                        // Save the booked slot and other available slots into Redux state
                        dispatch(updateTcuBookedSlot(bookedSlot));
                        const otherSlots = availableSlots.slice(1); // exclude the 1st slot that is booked
                        dispatch(updateTcuOtherSlots(otherSlots));
                        navigate(
                          PATHS.APPOINTMENT_CREATE_TCU_AVAILABLE_SLOTS.path,
                        );
                      }}
                    />
                  </Box>
                )
              }
            </Box>
            <Box sx={{ m: 2 }}>
              <ButtonsFooter
                nextButtonText="Confirm"
                cancelButtonText="Cancel this appointment"
                onClickNext={() => {
                  logEventToGoogleAnalytics(
                    EVENTS.CLICK_TCU_APPT_OTHER_CONFIRM,
                  );
                  setOpen(true);
                }}
                onClickCancel={handleCancelAppt}
              />
            </Box>
          </VerticalSpreadLayout>
          {remainingRegInstitutionCode &&
          remainingRegInstitutionCode?.length > 1 ? (
            // Prompt user to book tcu appointment for the other domain in the same insitution
            <ConfirmationModal
              open={open}
              title="You Have Another Appointment to Confirm"
              hideNextButton={false}
              nextButtonText="Proceed to confirm"
              cancelButtonText="Back to Appointment"
              onClickNext={() => {
                const nextRegInstitutionCode = remainingRegInstitutionCode.find(
                  (x) => x !== selectedRegInstitutionCode,
                );
                dispatch(
                  setTcuSelectedRegInstitutionCode(
                    nextRegInstitutionCode || "",
                  ),
                );
                dispatch(
                  updateTcuRemainingRegInstitutionCodes([
                    nextRegInstitutionCode || "",
                  ]),
                );
                // repeat the process
                setOpen(false);
                setPageRefresh(true);
              }}
              onClickCancel={() => {
                navigate(PATHS.APPOINTMENT_MOBILE.path, { replace: true });
              }}
            />
          ) : (
            <ConfirmationModal
              open={open}
              title="Appointment Booked"
              hideCancelButton={true}
              nextButtonText="Go to Appointment"
              onClickNext={() => {
                navigate(PATHS.APPOINTMENT_MOBILE.path, {
                  replace: true,
                });
              }}
            />
          )}
        </>
      )}
    </Page>
  );
};

export default TcuConfirmationPage;
